import React, { useCallback, useMemo } from "react";
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { difference, isEmpty } from "lodash";

import { ReferencePickerPlugin } from "@adsk/acc-reference-picker";
import Checkbox from "@adsk/alloy-react-checkbox";
import { useLocale } from "@adsk/alloy-react-locale";
import {
  LoadMoreDataRow,
  TableCell,
  TableHeader,
  TableHeaderGroup,
  TableRow,
  useTable,
} from "@adsk/alloy-react-table";

import {
  StickyHeader,
  StickyHeaderTable,
} from "../../components/commonStyledComponents";
import { SystemWithCategoryAndStatus } from "../../models/systems";
import BaseCell from "../shared/DataView/BaseCell";
import CategoryCell from "../shared/DataView/CategoryCell";
import StatusCell from "../shared/DataView/StatusCell";

const defaultColumn: Partial<ColumnDef<SystemWithCategoryAndStatus>> = {
  minSize: 80,
  /* Initial width, but will grow proportionally within container */
  size: 80,
  enableSorting: false,
} as const;
const getRowId = (asset: SystemWithCategoryAndStatus) => asset.id;
const mapifyArray = (arr: string[]) =>
  arr.reduce<Record<string, true>>((map, item) => {
    map[item] = true;
    return map;
  }, {});

const SINGLE_SELECT = 1;

const SystemsCustomDataView: Exclude<
  ReferencePickerPlugin<SystemWithCategoryAndStatus>["CustomDataView"],
  undefined
> = ({
  disabledIds = [],
  selectedIds = [],
  entities: systems = [],
  onLoadMore,
  onChange,
  multiple: isMultiSelectable,
  error,
  filters: { searchExpression },
  setSelection,
  onTryAgain,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const disabledRowIds = mapifyArray(disabledIds);
  const selectedRowIds = mapifyArray(selectedIds);
  const selectableRows = systems.filter((system) => !disabledRowIds[system.id]);
  const selectedSystems =
    isEmpty(selectedRowIds) || !selectedRowIds
      ? []
      : selectableRows.filter((system) => selectedRowIds[system.id]);
  const isAllRowsSelected =
    selectableRows.length === selectedSystems.length &&
    selectedSystems.length > 0;

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<SystemWithCategoryAndStatus>();

    return [
      {
        id: "select",
        size: 52,
        enableResizing: false,
        header: ({ table }) => (
          <Checkbox
            // The alloy table provides methods to determine the status of
            // this checkbox (getIsSomePageRowsSelected, getIsAllRowsSelected,
            // etc), but alloy table does not support our use case where a
            // disabled item is selected. This use case was established to
            // follow the pattern of other plugins in the reference picker.
            // If at some point it becomes possible to use alloy table methods
            // we can change this.
            checked={
              selectedSystems.length && !isAllRowsSelected
                ? "indeterminate"
                : isAllRowsSelected
            }
            onChange={(_val, e) => {
              const toggleAll = table.getToggleAllRowsSelectedHandler();
              toggleAll?.(e);
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={
              row.getIsSomeSelected() ? "indeterminate" : row.getIsSelected()
            }
            disabled={!row.getCanSelect()}
          />
        ),
      } as ColumnDef<SystemWithCategoryAndStatus, string>,
      columnHelper.accessor("name", {
        header: T.translate("SYSTEM_NAME") as string,
        cell: (props: CellContext<SystemWithCategoryAndStatus, string>) => (
          <BaseCell {...props} searchExpression={searchExpression} />
        ),
        size: 250,
      }),
      columnHelper.accessor("categoryId", {
        header: T.translate("CATEGORY") as string,
        cell: CategoryCell,
        size: 200,
      }),
      columnHelper.accessor("statusId", {
        header: T.translate("STATUS") as string,
        cell: StatusCell,
        size: 200,
      }),
      columnHelper.accessor("description", {
        header: T.translate("DESCRIPTION") as string,
        cell: (props: CellContext<SystemWithCategoryAndStatus, string>) => (
          <BaseCell {...props} searchExpression={searchExpression} />
        ),
        size: 200,
      }),
    ];
  }, [T, searchExpression, selectedSystems.length, isAllRowsSelected]);

  const onRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((rowSelectionState: RowSelectionState) => RowSelectionState)
    ) => {
      const selectedIdsEnabled = selectedIds.filter(
        (id) => !disabledRowIds[id]
      );
      const rowSelectionState =
        typeof updaterOrValue === "function"
          ? updaterOrValue(selectedRowIds)
          : updaterOrValue;
      const rowSelectionStateKeys = Object.keys(rowSelectionState);
      const addedIds = difference(rowSelectionStateKeys, selectedIds);
      const removedIds = difference(selectedIdsEnabled, rowSelectionStateKeys);

      if (
        addedIds.length === SINGLE_SELECT ||
        removedIds.length === SINGLE_SELECT
      ) {
        const entity = systems.find(
          (system) => system.id === addedIds[0] || system.id === removedIds[0]
        );

        return entity && onChange({ entity, checked: addedIds.length === 1 });
      }

      const systemsToSelect = selectedIdsEnabled.length
        ? []
        : systems.filter(({ id }) => !disabledRowIds[id]);

      setSelection(systemsToSelect);
    },
    [
      onChange,
      systems,
      disabledRowIds,
      setSelection,
      selectedRowIds,
      selectedIds,
    ]
  );

  const renderLastRow = useCallback(() => {
    const onLoad = onLoadMore
      ? async () => onLoadMore()
      : error
        ? async () => onTryAgain()
        : undefined;

    return !onLoad ? null : (
      <LoadMoreDataRow
        // Commented out the isAuto because it caused duplicate network requests.
        // We're soon to replace this mechanism with pagination, so it's not worth fixing.
        // isAuto
        hasError={!!error}
        onRetry={async () => onTryAgain()}
        onLoad={onLoad}
      />
    );
  }, [onTryAgain, onLoadMore, error]);

  const table = useReactTable({
    state: {
      rowSelection: selectedRowIds,
    },
    data: systems,
    columns,
    defaultColumn,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => !disabledRowIds[row.id],
    enableMultiRowSelection: isMultiSelectable,
    getRowId,
    columnResizeMode: "onChange",
    onRowSelectionChange,
  });
  const {
    getTableCellProps,
    getTableHeaderProps,
    getTableHeaderGroupProps,
    getTableRowProps,
  } = useTable({ table });

  return (
    <>
      <StickyHeaderTable>
        <StickyHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderGroup
              key={headerGroup.id}
              {...getTableHeaderGroupProps(headerGroup)}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id} {...getTableHeaderProps(header)} />
              ))}
            </TableHeaderGroup>
          ))}
        </StickyHeader>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              disabled={disabledRowIds[row.id]}
              {...getTableRowProps(row)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} {...getTableCellProps(cell)} />
              ))}
            </TableRow>
          ))}
        </tbody>
      </StickyHeaderTable>
      {renderLastRow()}
    </>
  );
};

SystemsCustomDataView.displayName = "SystemsCustomDataView";

export default SystemsCustomDataView;
